import { LinearProgress } from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { showFailureToast, showSuccessToast } from 'lib/ToastHelper';
import React, { useState, useEffect, useRef } from 'react';
import { Button, Card } from 'reactstrap';
import { companyIdAtom } from 'views/state';

import { checkProvisioningStatus as checkProvisioningService } from '../../../services/integrationListingService';
import { integrationDataRefreshAtom } from '../../../state';
import { StepComponentProps } from '../StepperModal';
import './IntegrationSteps.scss';

const ProvisioningCard = () => {
  return (
    <Card className="aws-cf-provisioning-card mb-4">
      <div className="aws-cf-provisioning-body">
        <div className="aws-cf-provisioning-header">
          <div className="aws-cf-provisioning-copy">
            <h6 className="aws-cf-provisioning-title">
              Provisioning in progress
              <LinearProgress color="info" />
            </h6>
            <p className="aws-cf-provisioning-description">
              Waiting for CloudFormation stack to complete.
            </p>
          </div>
        </div>
        <ul className="aws-cf-provisioning-list">
          <li className="aws-cf-provisioning-list-item">
            CloudFormation stack is being created in your AWS account.
          </li>
          <li className="aws-cf-provisioning-list-item">
            This may take a few minutes - please keep this window open.
          </li>
          <li className="aws-cf-provisioning-list-item">
            Once the stack creation completes successfully, your integration
            will be ready to use.
          </li>
        </ul>
      </div>
    </Card>
  );
};

const AWSCloudFormationStep: React.FC<StepComponentProps> = ({
  description,
  data,
  onValidationChange,
  onClose,
  loading,
  allowBack,
}) => {
  const [isProvisioning, setIsProvisioning] = useState(false);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const companyId = useAtomValue(companyIdAtom);
  const refreshGrid = useSetAtom(integrationDataRefreshAtom);
  /**
   * Polls the backend API to check CloudFormation stack status
   */
  const checkProvisioningStatus = async () => {
    try {
      // Replace with your actual API endpoint and integration ID field
      // Using type assertion as the specific ID field may vary
      const integrationId = (data as Record<string, unknown>).integrationId as
        string | undefined;
      if (!integrationId) {
        return;
      }

      const response = await checkProvisioningService(integrationId, companyId);
      if (response.data.isActive) {
        // Stack is ready
        stopPolling();
        showSuccessToast(
          'CloudFormation stack created successfully! Integration is active.',
        );
        // Trigger grid re-fetch before closing
        refreshGrid(new Date().toISOString());
        onClose();
      } else if (response.data.errorDetails) {
        // Stack creation failed
        stopPolling();
        showFailureToast(
          `CloudFormation stack creation failed. ${response.data.errorDetails?.message || ''}`,
        );
        onClose();
      }
    } catch (error) {
      // API failed
      stopPolling();
      onClose(); // Close the modal on error to allow retrying the integration process
      const apiError = error as { status?: number; data?: { error?: string } };
      if (apiError.status === 409) {
        showFailureToast(
          `${apiError.data?.error || 'Failed to check provisioning status. Please try again.'}`,
        );
      } else {
        showFailureToast(
          'Failed to check provisioning status. Please try again.',
        );
      }
    }
  };

  /**
   * Starts polling the backend API every 10 seconds
   */
  const startPolling = () => {
    // Initial check
    void checkProvisioningStatus();

    // Set up interval for subsequent checks (every 10 seconds)
    pollingIntervalRef.current = setInterval(() => {
      void checkProvisioningStatus();
    }, 10000);
  };

  /**
   * Stops the polling interval
   */
  const stopPolling = () => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
  };

  /**
   * CloudFormation step is always valid (no user input required)
   * Notify parent on mount that step is valid
   */
  useEffect(() => {
    onValidationChange(true);
  }, [onValidationChange]);

  /**
   * Clean up polling when component unmounts or modal closes
   */
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, []);

  const handleBack = () => {
    // Stop polling before closing
    stopPolling();
    // Close the modal and reset data
    onClose();
  };

  /**
   * Handles form submission and navigation to next step
   * Opens CloudFormation link and starts provisioning status check
   */
  const handleNext = () => {
    // Validation: Check required fields
    if (data.cfsLink) {
      // Open a new tab with the Cloudformation stack link
      window.open(data.cfsLink, '_blank');

      // Start showing provisioning card
      setIsProvisioning(true);

      // Start polling for status
      startPolling();
    }
  };

  return (
    <div className="step-content-wrapper">
      {/* Zone 1 – Step description (pinned header) */}
      <div className="step-description-header">
        <h5>{description ?? 'Cloud Formation Stack and Provisioning'}</h5>
      </div>

      {/* Zone 2 – Scrollable body */}
      <div className="step-form-body">
        {!isProvisioning ? (
          <>
            <p className="text-muted mb-4">
              Your AWS account will be added using AWS’s recommended best
              practice of granting access using IAM roles. All required
              resources and access will be provisioned via a Cloudformation
              stack. Click below and follow the instructions to complete the
              integration.
              <br />
              <strong className="d-block mt-2 font-weight-bold">
                Please Note: A security scan will start automatically upon setup
                completion.
              </strong>
            </p>
          </>
        ) : (
          <ProvisioningCard />
        )}
      </div>

      {/* Zone 3 – Action buttons (pinned footer) */}
      <div
        className={`step-action-footer d-flex ${
          allowBack ? 'justify-content-between' : 'justify-content-end'
        }`}
      >
        {allowBack && (
          <Button color="secondary" onClick={handleBack} disabled={loading}>
            Cancel Integration
          </Button>
        )}
        {!isProvisioning && (
          <Button color="primary" onClick={handleNext} disabled={loading}>
            {loading ? 'Processing...' : 'Launch Cloudformation Stack'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default AWSCloudFormationStep;
